import React, { useState, useEffect } from 'react';
import { stripeConfigService } from '../../../services/stripeConfigService';
import { StripeConfig } from '../../../types';
import Button from '../../atoms/Button';
import Input from '../../atoms/Input';
import Toggle from '../atoms/Toggle';
import './StripeConfigForm.css';

interface StripeConfigFormProps {
  onConfigUpdated?: (config: StripeConfig) => void;
  className?: string;
}

export const StripeConfigForm: React.FC<StripeConfigFormProps> = ({ 
  onConfigUpdated, 
  className = '' 
}) => {
  const [config, setConfig] = useState<Partial<StripeConfig>>({
    publishableKey: '',
    secretKey: '',
    webhookSecret: '',
    environment: 'test',
    isActive: true
  });
  
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showSecretKey, setShowSecretKey] = useState(false);
  const [showWebhookSecret, setShowWebhookSecret] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  // Load existing configuration
  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      setLoading(true);
      const existingConfig = await stripeConfigService.getConfig();
      
      if (existingConfig) {
        setConfig({
          publishableKey: existingConfig.publishableKey,
          secretKey: existingConfig.secretKey,
          webhookSecret: existingConfig.webhookSecret || '',
          environment: existingConfig.environment,
          isActive: existingConfig.isActive
        });
      }
    } catch (error) {
      console.error('Error loading config:', error);
      setError('Failed to load existing configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof StripeConfig, value: string | boolean) => {
    setConfig(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear errors when user starts typing
    if (error) setError(null);
    if (success) setSuccess(null);
    if (validationErrors.length > 0) setValidationErrors([]);
  };

  const validateConfig = (): boolean => {
    const validation = stripeConfigService.validateConfig(config);
    setValidationErrors(validation.errors);
    return validation.isValid;
  };

  const handleTestConnection = async () => {
    if (!validateConfig()) {
      return;
    }

    try {
      setTesting(true);
      setError(null);
      
      const isValid = await stripeConfigService.testConnection(config);
      
      if (isValid) {
        setSuccess('Connection test successful! Stripe keys are valid.');
      } else {
        setError('Connection test failed. Please check your API keys.');
      }
    } catch (error) {
      console.error('Connection test error:', error);
      setError('Connection test failed. Please try again.');
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async () => {
    if (!validateConfig()) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const updatedConfig = await stripeConfigService.updateConfig({
        ...config,
        updatedBy: 'admin' // In a real app, get from auth context
      });
      
      setSuccess('Stripe configuration saved successfully!');
      onConfigUpdated?.(updatedConfig);
      
      // Clear sensitive fields from form
      setConfig(prev => ({
        ...prev,
        secretKey: '',
        webhookSecret: ''
      }));
    } catch (error) {
      console.error('Save error:', error);
      setError('Failed to save configuration. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setConfig({
      publishableKey: '',
      secretKey: '',
      webhookSecret: '',
      environment: 'test',
      isActive: true
    });
    setError(null);
    setSuccess(null);
    setValidationErrors([]);
  };

  const formatKey = (key: string, show: boolean): string => {
    if (!key) return '';
    if (show) return key;
    return key.substring(0, 8) + '••••••••••••••••••••••••••••••••••••••••';
  };

  return (
    <div className={`stripe-config-form ${className}`}>
      <div className="stripe-config-form__header">
        <h3 className="stripe-config-form__title">Stripe Configuration</h3>
        <p className="stripe-config-form__description">
          Configure your Stripe API keys to enable payment processing. 
          Keys are encrypted and stored securely.
        </p>
      </div>

      {error && (
        <div className="stripe-config-form__error">
          <div className="stripe-config-form__error-icon">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M12 9V13M12 17H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <span>{error}</span>
        </div>
      )}

      {success && (
        <div className="stripe-config-form__success">
          <div className="stripe-config-form__success-icon">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <span>{success}</span>
        </div>
      )}

      {validationErrors.length > 0 && (
        <div className="stripe-config-form__validation-errors">
          <h4>Please fix the following errors:</h4>
          <ul>
            {validationErrors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="stripe-config-form__fields">
        <div className="stripe-config-form__field">
          <label className="stripe-config-form__label">
            Environment
          </label>
          <div className="stripe-config-form__environment-toggle">
            <button
              type="button"
              className={`stripe-config-form__env-button ${
                config.environment === 'test' ? 'stripe-config-form__env-button--active' : ''
              }`}
              onClick={() => handleInputChange('environment', 'test')}
            >
              Test Mode
            </button>
            <button
              type="button"
              className={`stripe-config-form__env-button ${
                config.environment === 'live' ? 'stripe-config-form__env-button--active' : ''
              }`}
              onClick={() => handleInputChange('environment', 'live')}
            >
              Live Mode
            </button>
          </div>
          <p className="stripe-config-form__field-help">
            Use test mode for development and testing. Switch to live mode for production.
          </p>
        </div>

        <div className="stripe-config-form__field">
          <label className="stripe-config-form__label">
            Publishable Key
          </label>
          <Input
            type="text"
            value={config.publishableKey || ''}
            onChange={(e) => handleInputChange('publishableKey', e.target.value)}
            placeholder="pk_test_..."
            className="stripe-config-form__input"
          />
          <p className="stripe-config-form__field-help">
            Your Stripe publishable key (starts with pk_test_ or pk_live_)
          </p>
        </div>

        <div className="stripe-config-form__field">
          <label className="stripe-config-form__label">
            Secret Key
          </label>
          <div className="stripe-config-form__secret-input">
            <Input
              type={showSecretKey ? 'text' : 'password'}
              value={formatKey(config.secretKey || '', showSecretKey)}
              onChange={(e) => handleInputChange('secretKey', e.target.value)}
              placeholder="sk_test_..."
              className="stripe-config-form__input"
            />
            <button
              type="button"
              className="stripe-config-form__toggle-visibility"
              onClick={() => setShowSecretKey(!showSecretKey)}
            >
              {showSecretKey ? 'Hide' : 'Show'}
            </button>
          </div>
          <p className="stripe-config-form__field-help">
            Your Stripe secret key (starts with sk_test_ or sk_live_). This will be encrypted.
          </p>
        </div>

        <div className="stripe-config-form__field">
          <label className="stripe-config-form__label">
            Webhook Secret (Optional)
          </label>
          <div className="stripe-config-form__secret-input">
            <Input
              type={showWebhookSecret ? 'text' : 'password'}
              value={formatKey(config.webhookSecret || '', showWebhookSecret)}
              onChange={(e) => handleInputChange('webhookSecret', e.target.value)}
              placeholder="whsec_..."
              className="stripe-config-form__input"
            />
            <button
              type="button"
              className="stripe-config-form__toggle-visibility"
              onClick={() => setShowWebhookSecret(!showWebhookSecret)}
            >
              {showWebhookSecret ? 'Hide' : 'Show'}
            </button>
          </div>
          <p className="stripe-config-form__field-help">
            Webhook endpoint secret for verifying webhook signatures (starts with whsec_)
          </p>
        </div>

        <div className="stripe-config-form__field">
          <Toggle
            id="stripe-active-toggle"
            checked={config.isActive || false}
            onChange={(checked: boolean) => handleInputChange('isActive', checked)}
            label="Enable Stripe Payments"
            description="Turn on/off payment processing"
          />
        </div>
      </div>

      <div className="stripe-config-form__actions">
        <Button
          variant="secondary"
          onClick={handleTestConnection}
          disabled={testing || loading}
          className="stripe-config-form__test-button"
        >
          {testing ? 'Testing...' : 'Test Connection'}
        </Button>
        
        <div className="stripe-config-form__save-actions">
          <Button
            variant="secondary"
            onClick={handleReset}
            disabled={loading}
            className="stripe-config-form__reset-button"
          >
            Reset
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={loading || testing}
            className="stripe-config-form__save-button"
          >
            {loading ? 'Saving...' : 'Save Configuration'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default StripeConfigForm;
